import { motion } from 'framer-motion';
import { useState } from 'react';
import { ArrowRight } from '@phosphor-icons/react';
import { useLanguage } from '@/i18n/LanguageContext';
import { toast } from 'sonner';
import { API_URL } from '@/utils/apiUrl';


export default function ContactSection() {
  const { language } = useLanguage();
  const isTurkish = language === 'tr';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setIsSuccess(true);
        setFormData({ name: '', email: '', message: '' });
        toast.success(isTurkish ? 'Mesajınız gönderildi — en kısa sürede dönüş yapacağız.' : 'Message sent — we will get back to you shortly.');
      } else {
        toast.error(isTurkish ? 'Mesaj gönderilemedi, lütfen tekrar deneyin.' : 'Message could not be sent, please try again.');
      }
    } catch (err) {
      console.error('Contact form error:', err);
      toast.error(isTurkish ? 'Bağlantı hatası — lütfen tekrar deneyin.' : 'Connection error — please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 bg-[#0D0D0D] relative" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <span className="inline-block px-3 py-1.5 border border-white/10 text-xs font-mono uppercase tracking-wider text-white/50 mb-6">
                {isTurkish ? 'İletişim' : 'Contact'}
              </span>
              <h2 className="text-3xl lg:text-4xl font-light text-white tracking-tight mb-4">
                {isTurkish ? 'Geleceği Birlikte ' : "Let's Build the "}
                <span className="font-bold">{isTurkish ? 'İnşa Edelim' : 'Future Together'}</span>
              </h2>
              <p className="text-white/50 max-w-md">
                {isTurkish 
                  ? 'Demo talep edin, yatırım fırsatlarını konuşalım veya enterprise çözümlerimizi keşfedin.'
                  : 'Request a demo, discuss investment opportunities, or explore our enterprise solutions.'}
              </p>
            </div>

            {/* Contact Info */}
            <div className="space-y-4 pt-8 border-t border-white/10">
              <a href="mailto:alihan@nexusdesignai.com" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors group" data-testid="contact-email">
                <span className="w-10 h-10 border border-white/10 flex items-center justify-center group-hover:border-white/30 transition-colors">
                  <span className="text-sm">@</span>
                </span>
                <span>alihan@nexusdesignai.com</span>
              </a>
              <div className="flex items-center gap-3 text-white/70" data-testid="contact-address">
                <span className="w-10 h-10 border border-white/10 flex items-center justify-center">
                  <span className="text-sm">⌂</span>
                </span>
                <span>North Ridgeville, Ohio — USA</span>
              </div>
              <div className="flex items-center gap-3 text-white/70" data-testid="contact-founder">
                <span className="w-10 h-10 border border-white/10 flex items-center justify-center">
                  <span className="text-sm">◎</span>
                </span>
                <span>
                  <span className="text-white">Alihan Demirhan</span>
                  <span aria-hidden="true" className="text-white/30 mx-1.5">·</span>
                  <span className="text-white/40 text-xs font-mono uppercase tracking-wider">
                    {isTurkish ? 'Kurucu' : 'Founder'}
                  </span>
                </span>
              </div>
              <a href="/pricing" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors group">
                <span className="w-10 h-10 border border-white/10 flex items-center justify-center group-hover:border-white/30 transition-colors">
                  <span className="text-sm">$</span>
                </span>
                <span>{isTurkish ? 'Fiyatlandırmayı Görüntüle' : 'View Pricing'}</span>
              </a>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {isSuccess ? (
              <div className="bg-[#060710] border border-white/10 p-12 text-center">
                <div className="w-16 h-16 border border-[#27C93F] flex items-center justify-center mx-auto mb-6">
                  <span className="text-[#27C93F] text-2xl">✓</span>
                </div>
                <h3 className="text-xl font-medium text-white mb-2">
                  {isTurkish ? 'Mesajınız İletildi' : 'Message Sent'}
                </h3>
                <p className="text-white/50 text-sm">
                  {isTurkish ? 'En kısa sürede size dönüş yapacağız.' : "We'll get back to you shortly."}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} method="post" action="#contact" className="bg-[#060710] border border-white/10 p-8 space-y-6" aria-label={isTurkish ? 'İletişim formu' : 'Contact form'}>
                <div>
                  <label htmlFor="contact-name" className="block text-xs text-white/40 uppercase tracking-wider mb-2">
                    {isTurkish ? 'İsim' : 'Name'}
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/30 focus:border-white/50 outline-none transition-colors"
                    placeholder={isTurkish ? 'Adınız Soyadınız' : 'Your full name'}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="contact-email" className="block text-xs text-white/40 uppercase tracking-wider mb-2">
                    {isTurkish ? 'E-posta' : 'Email'}
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/30 focus:border-white/50 outline-none transition-colors"
                    placeholder={isTurkish ? 'ornek@sirket.com' : 'you@company.com'}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="contact-message" className="block text-xs text-white/40 uppercase tracking-wider mb-2">
                    {isTurkish ? 'Mesaj' : 'Message'}
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={4}
                    className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/30 focus:border-white/50 outline-none transition-colors resize-none"
                    placeholder={isTurkish ? 'Projeniz veya ihtiyaçlarınız hakkında...' : 'Tell us about your project or needs...'}
                    required
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  aria-label={isTurkish ? 'İletişim formunu gönder' : 'Send contact message'}
                  data-testid="contact-submit-btn"
                  className="w-full flex items-center justify-center gap-3 bg-white text-black py-4 font-medium hover:bg-white/90 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="animate-pulse">{isTurkish ? 'Gönderiliyor...' : 'Sending...'}</span>
                  ) : (
                    <>
                      {isTurkish ? 'Gönder' : 'Send Message'}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>

        </div>
      </div>
    </section>
  );
}
